// src/TravelList.js

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth, useTrip } from '@trip-planner-monorepo/shared';
import { travelService } from '../services/travelService';
import AddTravel from './AddTravel/AddTravel';
import TravelDetails from './TravelDetails/TravelDetails';
import { FaPlus, FaTrash, FaEdit, FaPlane, FaTrain, FaCar, FaShip, FaBus, FaQuestion } from 'react-icons/fa';
import './TravelList.css';
import '@trip-planner-monorepo/shared/components/styles/actionButtons.css';

function TravelList() {
    const [travel, setTravel] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingTravel, setEditingTravel] = useState(null);

    const { currentUser } = useAuth();
    const { tripId } = useParams();
    const { effectiveUserId } = useTrip();

    const fetchTravel = async () => {
        if (!currentUser || !tripId || !effectiveUserId) return;

        setLoading(true);
        try {
            console.log('Fetching travel items...');
            const fetchedTravel = await travelService.fetchTravel(effectiveUserId, tripId);
            console.log('Fetched travel items:', fetchedTravel);
            setTravel(fetchedTravel);
        } catch (err) {
            setError('Failed to load travel items. Please try again.');
            console.error('Error fetching travel:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTravel();
    }, [currentUser, tripId, effectiveUserId]);

    const handleEditTravel = (travelItem) => {
        setEditingTravel(travelItem);
        setShowAddModal(true);
    };

    const handleAddTravel = async (travelData) => {
        try {
            console.log('Adding travel with data:', travelData);

            if (editingTravel) {
                if (!travelData.summary && editingTravel.summary) {
                    travelData.summary = editingTravel.summary;
                }

                await travelService.updateTravel(effectiveUserId, tripId, editingTravel.id, travelData);
                console.log('Travel updated successfully, fetching updated list');
            } else {
                await travelService.createTravel(effectiveUserId, tripId, travelData);
                console.log('Travel added successfully, fetching updated list');
            }

            fetchTravel();
            setShowAddModal(false);
            setEditingTravel(null);
        } catch (error) {
            setError('Failed to create travel item. Please try again.');
            console.error('Error creating travel:', error);
        }
    };

    const handleCloseModal = () => {
        setShowAddModal(false);
        setEditingTravel(null);
    };

    const handleDeleteTravel = async (travelId) => {
        if (window.confirm('Are you sure you want to delete this travel item?')) {
            try {
                await travelService.deleteTravel(effectiveUserId, tripId, travelId);
                await fetchTravel();
            } catch (err) {
                setError('Failed to delete travel item. Please try again.');
                console.error('Error deleting travel:', err);
            }
        }
    };

    const formatDateRange = (details) => {
        if (!details) return 'Date not set';
        
        // For train, flight types the fields are named departureTime and arrivalTime
        // For other travel types they might have different field names
        const startDate = details.departureTime || details.pickupTime || null;
        const endDate = details.arrivalTime || details.returnTime || null;

        if (!startDate) return 'Date not set';
        
        const formatDate = (date) => new Date(date).toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });

        return endDate && endDate !== startDate 
            ? `${formatDate(startDate)} - ${formatDate(endDate)}`
            : formatDate(startDate);
    };

    // Function to get the icon based on travel type
    const getTravelIcon = (type) => {
        switch (type) {
            case 'flight':
                return <FaPlane />;
            case 'train':
                return <FaTrain />;
            case 'car':
                return <FaCar />;
            case 'cruise':
                return <FaShip />;
            case 'bus':
                return <FaBus />;
            default:
                return <FaQuestion />;
        }
    };

    if (loading) {
        return <div className="loading">Loading travel items...</div>;
    }

    return (
        <div className="travel-content">
            {error && <div className="error-message">{error}</div>}

            <div className="travel-header">
                <button 
                    onClick={() => setShowAddModal(true)}
                    className="new-travel-button"
                >
                    <FaPlus /> Add Travel
                </button>
            </div>

            {travel.length === 0 ? (
                <div className="no-travel">
                    <div className="no-travel-icon">✈️</div>
                    <p>No travel items yet! Click "Add Travel" to start planning.</p>
                </div>
            ) : (
                <div className="travel-grid">
                    {travel.map(item => (
                        <div key={item.id} className="travel-item">
                            <div className="travel-info">
                                <Link 
                                    to={`/trips/${tripId}/travel/${item.id}`}
                                    className="travel-summary"
                                >
                                    <span className="travel-type-icon">
                                        {getTravelIcon(item.type)}
                                    </span>
                                    {item.summary}
                                </Link>
                                <div className="travel-meta">
                                    <span>{formatDateRange(item.details)}</span>
                                    <span className={`travel-status status-${item.status.toLowerCase()}`}>
                                        {item.status}
                                    </span>
                                </div>
                            </div>
                            <div className="action-buttons">
                                <button
                                    onClick={() => handleEditTravel(item)}
                                    className="action-button action-button--edit"
                                    title="Edit travel"
                                >
                                    <FaEdit />
                                </button>
                                <button
                                    onClick={() => handleDeleteTravel(item.id)}
                                    className="action-button action-button--delete"
                                    title="Delete travel"
                                >
                                    <FaTrash />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <AddTravel
                isOpen={showAddModal}
                onClose={handleCloseModal}
                onSave={handleAddTravel}
                initialTravel={editingTravel}
            />
        </div>
    );
}

export default TravelList;